import { validateApplication } from 'api/ats';
import type { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import type { ApiRequestedAttachment } from 'api/types/ApiRequestedAttachment';
import type { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';
import { Box } from 'components/Box/Box';
import { CheckboxField } from 'components/Form/CheckboxField';
import { FilePickerField } from 'components/Form/FileUploadField';
import { useFormContext } from 'components/Form/FormContext';
import { HiddenField } from 'components/Form/HiddenField';
import { InputField } from 'components/Form/InputField';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { useI18n } from 'i18n/hooks/useI18n';
import { RequestedAttachmentUserField } from 'modules/listing/components/ApplicationFields/RequestedAttachmentUserField';
import { RequestedTextUserField } from 'modules/listing/components/ApplicationFields/RequestedTextUserField';
import { RequestedURLUserField } from 'modules/listing/components/ApplicationFields/RequestedURLUserField';

import { RequestedMultipleOptionField } from './RequestedMultipleOptionField';
import { RequestedPhoneUserField } from './RequestedPhoneUserField';
import { RequestedSingleOptionField } from './RequestedSingleOptionField';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNewResume = (user: ApiUser | null | undefined, getValue: any) =>
  user?.resumeId &&
  getValue('resumeS3FileId') &&
  getValue('resumeS3FileId') !== user.resumeId;

type Props = {
  user: ApiUser | null;
  listingType: ApiListingApplicationListingType;
  isPreview: boolean;
  allFieldsRequired: boolean;
  requestedAttachments: ApiRequestedAttachment[];
  orgAdminManuallyAddingApplicant?: boolean;
};

export function ListingApplicationFields({
  user,
  listingType,
  isPreview,
  allFieldsRequired,
  requestedAttachments,
  orgAdminManuallyAddingApplicant,
}: Props) {
  const { i18n } = useI18n();

  const { handleError, getValue, updateValues, clearErrors } = useFormContext();

  return (
    <>
      <HiddenField name="requestedAttachmentsHash" />

      <InputField
        disabled={isPreview}
        name="firstName"
        label={
          !allFieldsRequired
            ? `${i18n('First Name')}*`
            : `${i18n('First Name')}`
        }
        placeholder={i18n('Casey')}
      />

      <InputField
        disabled={isPreview}
        name="lastName"
        label={
          !allFieldsRequired ? `${i18n('Last Name')}*` : `${i18n('Last Name')}`
        }
        placeholder={i18n('Smith')}
      />

      <InputField
        disabled={isPreview}
        name="email"
        label={!allFieldsRequired ? `${i18n('Email')}*` : `${i18n('Email')}`}
        type="email"
        placeholder={i18n('casey.smith@example.com')}
      />

      {listingType !== 'VOLOP' ? (
        <FilePickerField
          disabled={isPreview}
          label={
            !allFieldsRequired ? `${i18n('Resume')}*` : `${i18n('Resume')}`
          }
          description={i18n('Resume must be uploaded in PDF format')}
          downloadAttachment={false}
          name="resumeS3FileId"
          fileType={
            orgAdminManuallyAddingApplicant
              ? 'LISTING_APPLICATION'
              : 'USER_RESUME'
          }
          accept=".pdf"
          showDate={!isNewResume(user, getValue)}
          onUpdateValue={(fileId) => {
            if (!orgAdminManuallyAddingApplicant) {
              validateApplication({ resumeS3FileId: fileId }, { partial: true })
                .then(() => {
                  updateValues({ resumeS3FileId: fileId });
                  clearErrors();
                  return;
                })
                .catch((error) => {
                  handleError(error as Error, { resumeS3FileId: fileId });
                });
            } else {
              updateValues({ resumeS3FileId: fileId });
              clearErrors();
            }
          }}
          extraAnalyticsData={{
            context: 'Listing Application Form',
            listing_type: listingType,
            file_label: 'Resume',
          }}
        />
      ) : null}

      {user && isNewResume(user, getValue) && (
        <Box mt={-26}>
          <CheckboxField
            disabled={isPreview}
            name="replaceCurrentResume"
            label={
              <Box display="flex" alignItems="center">
                {i18n('Replace my current resume on file with this one')}
                <Box position="relative" zIndex={1} mt="1px" ml="6px">
                  <LegacyTooltip>
                    {i18n(`You already have a resume saved with your account.
                              If you check this box, we'll save this new resume
                              to your account.`)}
                  </LegacyTooltip>
                </Box>
              </Box>
            }
          />
        </Box>
      )}

      {requestedAttachments.map((requestedAttachment, index) => {
        if (requestedAttachment.type === 'TEXT') {
          return (
            <RequestedTextUserField
              key={[requestedAttachment.type, index].join('-')}
              name={`requestedAttachments.${index}`}
              label={
                !allFieldsRequired && requestedAttachment.isRequired
                  ? `${requestedAttachment.label}*`
                  : requestedAttachment.label
              }
              disabled={isPreview}
            />
          );
        }
        if (requestedAttachment.type === 'URL') {
          return (
            <RequestedURLUserField
              key={[requestedAttachment.type, index].join('-')}
              name={`requestedAttachments.${index}`}
              label={
                !allFieldsRequired && requestedAttachment.isRequired
                  ? `${requestedAttachment.label}*`
                  : requestedAttachment.label
              }
              disabled={isPreview}
              placeholder="https://example.org"
            />
          );
        }
        if (requestedAttachment.type === 'FILE') {
          return (
            <RequestedAttachmentUserField
              key={[requestedAttachment.type, index].join('-')}
              name={`requestedAttachments.${index}`}
              label={
                !allFieldsRequired && requestedAttachment.isRequired
                  ? `${requestedAttachment.label}*`
                  : requestedAttachment.label
              }
              disabled={isPreview}
              analyticsLabel={requestedAttachment.label}
              downloadAttachment={false}
              fileType="LISTING_APPLICATION"
              listingType={listingType}
            />
          );
        }

        if (requestedAttachment.type === 'PHONE') {
          return (
            <RequestedPhoneUserField
              key={[requestedAttachment.type, index].join('-')}
              name={`requestedAttachments.${index}`}
              label={
                !allFieldsRequired && requestedAttachment.isRequired
                  ? `${requestedAttachment.label}*`
                  : requestedAttachment.label
              }
              disabled={isPreview}
            />
          );
        }

        if (requestedAttachment.type === 'OPTION_SINGLE') {
          return (
            <RequestedSingleOptionField
              key={[requestedAttachment.type, index].join('-')}
              name={`requestedAttachments.${index}`}
              label={
                !allFieldsRequired && requestedAttachment.isRequired
                  ? `${requestedAttachment.label}*`
                  : requestedAttachment.label
              }
              disabled={isPreview}
              options={requestedAttachment.options}
            />
          );
        }
        if (requestedAttachment.type === 'OPTION_MANY') {
          return (
            <RequestedMultipleOptionField
              key={[requestedAttachment.type, index].join('-')}
              name={`requestedAttachments.${index}`}
              label={
                !allFieldsRequired && requestedAttachment.isRequired
                  ? `${requestedAttachment.label}*`
                  : requestedAttachment.label
              }
              disabled={isPreview}
              options={requestedAttachment.options}
            />
          );
        }
      })}
    </>
  );
}
